import { Injectable, DestroyRef, inject } from '@angular/core'
import { interval, catchError, of } from 'rxjs'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatDialog } from '@angular/material/dialog'
import { ToastService } from '@overtur-monorepo/keying-shared'
import { INotification } from '@overtur-monorepo/notification-management'
import { NotificationService } from './notification.service'
import { NotificationDetailDialogComponent } from '../dialogs/notification-detail-dialog/notification-detail-dialog.component'
import { FeatureManagementService } from '@overtur-monorepo/feature-management'

const DEFAULT_POLL_INTERVAL_MS = 60 * 60 * 1000

@Injectable({ providedIn: 'root' })
export class NotificationPollingService {
  private readonly destroyRef = inject(DestroyRef)
  private started = false
  private readonly toastRefs = new Map<number, any>()
  private lastPendingIds = new Set<number>()

  constructor(
    private notificationService: NotificationService,
    private toastService: ToastService,
    private matDialog: MatDialog,
    private featureManagementService: FeatureManagementService,
  ) {}

  start(): void {
    if (this.started) return
    this.started = true

    this.featureManagementService
      .getConfiguration('NOTIFICATION_VIEW')
      .pipe(catchError(() => of({})))
      .subscribe((config) => {
        const intervalMs = config?.['pollIntervalMs'] ?? DEFAULT_POLL_INTERVAL_MS
        this.fetchAndNotify()
        interval(intervalMs)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.fetchAndNotify())
      })
  }

  dismiss(id: number): void {
    if (!this.lastPendingIds.has(id)) {
      this.closeToast(id)
      return
    }
    this.notificationService.dismiss(id).pipe(
      catchError(() => of(void 0))
    ).subscribe(() => this.fetchAndNotify())
  }

  closeToast(id: number): void {
    this.toastRefs.get(id)?.close()
    this.toastRefs.delete(id)
  }

  refresh(): void {
    this.fetchAndNotify()
  }

  private fetchAndNotify(): void {
    this.notificationService.getPending().subscribe((notifications) => {
      const currentIds = new Set(notifications.map((n) => n.id))

      // Close toasts for notifications no longer in the pending list
      this.toastRefs.forEach((_, id) => {
        if (!currentIds.has(id)) this.closeToast(id)
      })

      this.lastPendingIds = currentIds
      notifications.forEach((n) => this.showNotificationToast(n))
    })
  }

  private showNotificationToast(notification: INotification): void {
    const ref = this.toastService.showToast({
      title: notification.title,
      subTitle: this.truncateAtWord(this.stripHtml(notification.content), 90),
      type: 'info',
      actionLabel: 'View Details',
      onAction: () => {
        this.toastRefs.get(notification.id)?.close()
        this.toastRefs.delete(notification.id)
        this.matDialog.open(NotificationDetailDialogComponent, {
          data: {
            notification,
            onAcknowledge: () => this.dismiss(notification.id),
          },
          width: '560px',
        })
      },
    })
    this.toastRefs.set(notification.id, ref)
  }

  private truncateAtWord(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text
    const lastSpace = text.lastIndexOf(' ', maxLength)
    const cut = lastSpace > 0 ? lastSpace : maxLength
    return text.substring(0, cut) + '...'
  }

  private stripHtml(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return (doc.documentElement.textContent ?? '').replace(/\s+/g, ' ').trim()
  }
}
